// ─────────────────────────────────────────────────────────
// store/authStore.ts — Authentication & user state
// Replaces: CU global, getRole(), isMasterAdmin() etc.
// ─────────────────────────────────────────────────────────
import { create } from 'zustand'
import type { AppUser, Role, Brand } from '@/types'

interface AuthState {
  user:        AppUser | null
  brand:       Brand
  isLoading:   boolean
  isOnline:    boolean

  // Actions
  setUser:    (user: AppUser | null) => void
  setBrand:   (brand: Brand) => void
  setLoading: (loading: boolean) => void
  setOnline:  (online: boolean) => void

  // Role helpers (replaces monolith global functions)
  getRole:        () => Role | null
  isPlatformAdmin:() => boolean
  isMasterAdmin:  () => boolean
  isAdmin:        () => boolean
  isAuditor:      () => boolean
  isViewer:       () => boolean
  canWrite:       () => boolean
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user:      null,
  brand:     { name: 'CivilBillDesk' },
  isLoading: true,
  isOnline:  navigator.onLine,

  setUser:    (user)    => set({ user }),
  setBrand:   (brand)   => set({ brand }),
  setLoading: (loading) => set({ isLoading: loading }),
  setOnline:  (online)  => set({ isOnline: online }),

  // ── Role helpers ──────────────────────────────────────
  getRole: () => get().user?.role ?? null,

  isPlatformAdmin: () => get().user?.role === 'super_admin',

  isMasterAdmin: () =>
    ['super_admin', 'master_admin'].includes(get().user?.role ?? ''),

  isAdmin: () =>
    ['super_admin', 'master_admin', 'admin'].includes(get().user?.role ?? ''),

  isAuditor: () => get().user?.role === 'auditor',

  isViewer: () => get().user?.role === 'viewer',

  canWrite: () =>
    !['viewer', 'auditor'].includes(get().user?.role ?? 'viewer'),
}))
